import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import dayjs from 'dayjs';
import useSWR from 'swr';
import axios from 'axios';
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useSelector } from "react-redux";
import { exportStyledExcel, pdfTableTheme } from '../../utils/exportHelpers';
import { apiHost } from '../../utils/apiHost';

const fetcher = (url) => axios.get(url, { withCredentials: true }).then((res) => res.data);

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};

export const PenjualanPerkategori = () => {
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM'));
  const [selectedCabang, setSelectedCabang] = useState('');
  const { user } = useSelector((state) => state.auth);

  const startDate = `${selectedMonth}-01`;
  const endDate = dayjs(startDate).endOf('month').format('YYYY-MM-DD');

  const { data: cabangList } = useSWR(
    user?.role === 'superadmin' ? `${getApiBaseUrl()}/cabang` : null,
    fetcher
  );

  const apiUrl = `${getApiBaseUrl()}/penjualan-per-kategori?startDate=${startDate}&endDate=${endDate}${selectedCabang ? `&cabangUuid=${selectedCabang}` : ''}`;
  const { data, error } = useSWR(user ? apiUrl : null, fetcher);

  // Handle loading state
  if (!data) return <CircularProgress />;
  if (error) return <Typography color="error">Gagal mengambil data.</Typography>;

  const { period, data: salesData } = data;

  // Keterangan export: tanggal mudah dibaca, nama cabang, dan total (sama dengan export di aplikasi).
  const tglIndo = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');
  const labelPeriode = `${tglIndo(period.startDate)} - ${tglIndo(period.endDate)}`;
  const namaCabang = user?.role === 'superadmin'
    ? (cabangList?.data?.find((c) => c.uuid === selectedCabang)?.namacabang || 'Semua cabang')
    : (user?.cabang?.namacabang || '-');
  const totalTerjual = salesData.reduce((a, s) => a + (Number(s.totalQuantity) || 0), 0);
  const totalPenjualan = salesData.reduce((a, s) => a + (Number(s.totalPenjualan) || 0), 0);
  const rp = (n) => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;

  // Fungsi untuk export PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const lebar = doc.internal.pageSize.getWidth();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Laporan Penjualan per Kategori', lebar / 2, 16, { align: 'center' });
    doc.setFontSize(10);
    doc.text('Periode', 14, 26);
    doc.text('Cabang', 14, 32);
    doc.setFont('helvetica', 'normal');
    doc.text(labelPeriode, 44, 26);
    doc.text(namaCabang, 44, 32);

    const tableColumn = ["Kategori", "Total Terjual", "Total Penjualan"];
    const tableRows = salesData.map(({ namaKategori, totalQuantity, totalPenjualan }) => [
      namaKategori, Number(totalQuantity || 0).toLocaleString('id-ID'), rp(totalPenjualan)
    ]);

    doc.autoTable({
      startY: 38,
      head: [tableColumn],
      body: tableRows,
      foot: [['TOTAL', totalTerjual.toLocaleString('id-ID'), rp(totalPenjualan)]],
      showFoot: 'lastPage',
      ...pdfTableTheme,
      styles: { ...pdfTableTheme.styles, halign: 'center', lineColor: [189, 189, 189], lineWidth: 0.2 },
      headStyles: { ...pdfTableTheme.headStyles, halign: 'center' },
      footStyles: { fillColor: [255, 213, 79], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
    });

    doc.save("penjualan_per_kategori.pdf");
  };

  const exportToExcel = async () => {
    const rows = salesData.map(({ namaKategori, totalQuantity, totalPenjualan }) => [
      namaKategori, Number(totalQuantity) || 0, Number(totalPenjualan) || 0
    ]);
    await exportStyledExcel({
      fileName: 'penjualan_per_kategori.xlsx',
      sheetName: 'Penjualan Per Kategori',
      title: 'Laporan Penjualan per Kategori',
      metaRows: [['Periode', labelPeriode], ['Cabang', namaCabang]],
      headers: ['Kategori', 'Total Terjual', 'Total Penjualan'],
      rows,
      columnWidths: [30, 16, 20],
      grandTotalRow: { label: 'TOTAL', values: [totalTerjual, totalPenjualan] },
    });
  };

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Laporan Penjualan per Kategori
      </Typography>

      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <TextField
            label="Pilih Bulan"
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />
        </Grid>
        {user?.role === 'superadmin' && (
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter Cabang</InputLabel>
              <Select
                value={selectedCabang}
                onChange={(e) => setSelectedCabang(e.target.value)}
                label="Filter Cabang"
              >
                <MenuItem value="">
                  <em>Semua Cabang</em>
                </MenuItem>
                {cabangList?.data?.map((cabang) => (
                  <MenuItem key={cabang.uuid} value={cabang.uuid}>
                    {cabang.namacabang}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item>
          <Button variant="contained" color="primary" onClick={exportToPDF}>
            Export PDF
          </Button>
        </Grid>
        <Grid item>
          <Button variant="contained" color="success" onClick={exportToExcel}>
            Export Excel
          </Button>
        </Grid>
      </Grid>

      <Typography variant="subtitle1" mt={2}>
        Periode: {period.startDate} - {period.endDate}
      </Typography>

      <Paper sx={{ mt: 3, p: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><b>Kategori</b></TableCell>
              <TableCell><b>Total Terjual</b></TableCell>
              <TableCell><b>Total Penjualan</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {salesData.length > 0 ? (
              salesData.map((row) => (
                <TableRow key={row.kategoriId}>
                  <TableCell>{row.namaKategori}</TableCell>
                  <TableCell>{row.totalQuantity}</TableCell>
                  <TableCell>Rp {row.totalPenjualan.toLocaleString()}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">Tidak ada data</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};
